import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Stat, Quiet, NotRunning, Button, C, errText } from "./ui.jsx";
import { QUICK, quickOf, todayIn, ymdOf, shortDate, showingLine, monthDays, MONTHS, WEEKDAYS, changeWords, count, clockOf } from "./reportsView.js";

/**
 * Call reports (the schematic's Settings · Call reports board, with Brian's 10-30 comments: a Today
 * option, a calendar and a time window; titles, not statements; trends).
 *
 * ⛔ EVERY NUMBER IS A COUNT OF CALLS THAT HAPPENED (backend reports.mjs): seeded and AI-persona calls
 * are left out. "Check calls on time" is not here: nothing records when a check call was due, so it
 * cannot be measured yet. Driver feedback needs Alfred to read what drivers say on calls, which is
 * not built, so that area is marked "Not running yet".
 */

const pressed = (on) => ({ minHeight: 44, padding: "0 16px", font: "inherit", fontSize: 15, fontWeight: 600, borderRadius: 8, cursor: "pointer",
  color: on ? "#FFFFFF" : C.ink, background: on ? C.accent : C.card, border: `1px solid ${on ? C.accent : C.field}` });

export function PeriodControl({ p, setP, today }) {
  const match = quickOf(p.start, p.end, today);
  const shown = p.calMonth ?? (new Date(p.end * 86400000).getUTCFullYear() * 12 + new Date(p.end * 86400000).getUTCMonth());
  const year = Math.floor(shown / 12), month = shown % 12;
  const lastMonth = shown >= new Date(today * 86400000).getUTCFullYear() * 12 + new Date(today * 86400000).getUTCMonth();
  const pick = (n) => {
    if (!p.pending) setP({ ...p, start: n, end: n, pending: true });
    else if (n < p.start) setP({ ...p, start: n, end: p.start, pending: false });
    else setP({ ...p, end: n, pending: false });
  };
  const timeError = p.between && p.to <= p.from;
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 id="period-title" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Period</h3>
        <div role="group" aria-labelledby="period-title" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {QUICK.map((q) => (
            <button key={q.key} type="button" aria-pressed={!p.calOpen && match?.key === q.key} style={pressed(!p.calOpen && match?.key === q.key)}
              onClick={() => setP({ ...p, start: today - q.days + 1, end: today, calOpen: false, pending: false })}>{q.label}</button>
          ))}
          <button type="button" aria-pressed={p.calOpen || !match} style={pressed(p.calOpen || !match)} aria-expanded={p.calOpen}
            onClick={() => setP({ ...p, calOpen: true, pending: false, calMonth: null })}>Pick dates</button>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span id="dates-label" style={{ fontSize: 14, fontWeight: 600 }}>Dates</span>
        <div role="group" aria-labelledby="dates-label" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" style={pressed(false)} aria-expanded={p.calOpen} onClick={() => setP({ ...p, calOpen: true, pending: false })}>{shortDate(p.start)}</button>
          <span>to</span>
          <button type="button" style={pressed(false)} aria-expanded={p.calOpen} onClick={() => setP({ ...p, calOpen: true, pending: false })}>{shortDate(p.end)}</button>
        </div>
        {p.calOpen && (
          <div role="dialog" aria-label="Pick dates" style={{ border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, maxWidth: 360, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button type="button" aria-label="Previous month" style={pressed(false)} onClick={() => setP({ ...p, calMonth: shown - 1 })}>‹</button>
              <span aria-live="polite" style={{ fontWeight: 600 }}>{`${MONTHS[month]} ${year}`}</span>
              <button type="button" aria-label="Next month" disabled={lastMonth} style={{ ...pressed(false), opacity: lastMonth ? 0.4 : 1 }} onClick={() => !lastMonth && setP({ ...p, calMonth: shown + 1 })}>›</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, textAlign: "center" }}>
              {WEEKDAYS.map((w) => <span key={w} style={{ fontSize: 13, color: C.muted }}>{w.slice(0, 3)}</span>)}
              {monthDays(year, month, { start: p.start, end: p.end, today }).map((d, i) => (d ? (
                <button key={i} type="button" disabled={d.later} onClick={() => pick(d.n)}
                  aria-label={`${WEEKDAYS[new Date(d.n * 86400000).getUTCDay()]}, ${MONTHS[month]} ${d.label}${d.isToday ? ", today" : ""}${d.inside ? ", picked" : ""}`}
                  style={{ minHeight: 40, border: 0, borderRadius: 8, font: "inherit", cursor: d.later ? "default" : "pointer", fontWeight: d.edge || d.isToday ? 600 : 400,
                    color: d.edge ? "#FFFFFF" : d.later ? C.faint : C.ink, background: d.edge ? C.accent : d.inside ? C.accentWash : "transparent",
                    boxShadow: d.isToday && !d.edge ? `inset 0 0 0 1px ${C.accent}` : "none" }}>{d.label}</button>
              ) : <span key={i} />))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, color: C.muted }}>{p.pending ? "Now pick the last day, or press Done for just that day." : "Pick the first day, then the last day."}</span>
              <Button kind="secondary" onClick={() => setP({ ...p, calOpen: false, pending: false })}>Done</Button>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span id="times-label" style={{ fontSize: 14, fontWeight: 600 }}>Times</span>
        <div role="radiogroup" aria-labelledby="times-label" style={{ display: "flex", gap: "8px 20px", flexWrap: "wrap", alignItems: "center", fontSize: 16 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="radio" name="report-times" checked={!p.between} onChange={() => setP({ ...p, between: false })} /> All day</label>
          <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="radio" name="report-times" checked={p.between} onChange={() => setP({ ...p, between: true })} /> Between</label>
            <input type="time" aria-label="Start time" value={p.from} onChange={(e) => e.target.value && setP({ ...p, from: e.target.value, between: true })}
              style={{ minHeight: 44, font: "inherit", fontSize: 15, border: `1px solid ${C.field}`, borderRadius: 8, padding: "0 8px" }} />
            <span>and</span>
            <input type="time" aria-label="End time" value={p.to} onChange={(e) => e.target.value && setP({ ...p, to: e.target.value, between: true })}
              style={{ minHeight: 44, font: "inherit", fontSize: 15, border: `1px solid ${C.field}`, borderRadius: 8, padding: "0 8px" }} />
          </span>
        </div>
        {timeError && <span role="alert" style={{ color: C.bad, fontSize: 15 }}>Pick an end time after the start time.</span>}
      </div>
      <p style={{ margin: 0, fontSize: 15, color: C.muted }}>{showingLine({ ...p, today })}</p>
    </Card>
  );
}

export function TodayCalls({ today }) {
  if (!today) return null;
  const groups = [["now", "On a call now"], ["next", "Coming up"], ["done", "Done"]].filter(([k]) => today[k]?.length);
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Today's calls</h3>
      {groups.length === 0 && <Quiet>No calls today in these hours.</Quiet>}
      {groups.map(([k, title]) => (
        <div key={k} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: C.muted }}>{title}</h4>
          {today[k].map((r) => (
            <div key={r.key} style={{ display: "flex", gap: "4px 16px", flexWrap: "wrap", alignItems: "baseline", fontSize: 16 }}>
              <span style={{ minWidth: 84, fontWeight: 600 }}>{r.at ? new Date(r.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : ""}</span>
              <span style={{ flex: "1 1 300px", minWidth: 0 }}>
                <span><strong>{r.who}</strong>{` · ${r.what}`}</span>
                {r.note && <span style={{ display: "block", fontSize: 15, color: k === "now" ? C.accent : C.muted }}>{r.note}</span>}
              </span>
              {r.href && <Link to={r.href} style={{ fontSize: 15, fontWeight: 600, textDecoration: "none" }}>Open</Link>}
            </div>
          ))}
        </div>
      ))}
    </Card>
  );
}

export function CallReportsView({ company, stage, p, setP, today, data, error }) {
  const days = p.end - p.start + 1;
  const t = data?.tiles;
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title sub="What Alfred's calls show.">Call reports</Title>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Calls</h2>
      <PeriodControl p={p} setP={setP} today={today} />
      {error && <Quiet>{`The numbers could not be read: ${error}`}</Quiet>}
      {!data && !error && <Quiet>Loading…</Quiet>}
      {t && (
        <section aria-label="Call totals" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Stat label="Calls Alfred made" value={count(t.made.cur)} sub={changeWords(t.made.cur, t.made.prev, { days })} />
          <Stat label="Driver calls picked up" value={t.pickedUp.cur == null ? "–" : `${t.pickedUp.cur}%`}
            sub={t.pickedUp.cur == null ? "No driver calls in this period" : changeWords(t.pickedUp.cur, t.pickedUp.prev, { days, unit: "points" })}
            tone={t.pickedUp.cur != null && t.pickedUp.prev != null ? (t.pickedUp.cur > t.pickedUp.prev ? "good" : t.pickedUp.cur < t.pickedUp.prev ? "bad" : null) : null} />
          <Stat label="Applicants who talked with Alfred" value={count(t.applicants.cur)} sub={changeWords(t.applicants.cur, t.applicants.prev, { days })} />
          {/* ⛔ TRIED, NOT PUT THROUGH: the line reports a transfer that failed, never one that connected (core freightTransferOutcome.js). */}
          <Stat label="Tried to put through to a person" value={count(t.putThrough.cur)}
            sub={t.putThrough.missed ? `${count(t.putThrough.missed)} did not reach the recruiter` : "To the recruiter, live"} />
        </section>
      )}
      {data?.today && <TodayCalls today={data.today} />}

      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>Driver feedback <NotRunning /></h2>
      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Company standards</h3>
          <p style={{ margin: 0, fontSize: 15, color: C.muted }}>Drivers who pushed back on each standard on Alfred's calls.</p>
        </div>
        <Quiet>No data yet.</Quiet>
      </Card>
      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Driver sentiment</h3>
          <p style={{ margin: 0, fontSize: 15, color: C.muted }}>Data from Alfred's driver call intake.</p>
        </div>
        <Quiet>No data yet.</Quiet>
      </Card>
    </Shell>
  );
}

export default function CallReports() {
  const { carrier } = useCarrier();
  const today = todayIn(carrier.timezone);
  const [p, setP] = useState({ start: today, end: today, calOpen: false, pending: false, calMonth: null, between: false, from: "09:00", to: "13:00" });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const timeError = p.between && p.to <= p.from;
  useEffect(() => {
    if (p.pending || timeError) return undefined;
    let alive = true;
    setError(null);
    freight.callReport({ from: ymdOf(p.start), to: ymdOf(p.end), start: p.between ? p.from : null, end: p.between ? p.to : null })
      .then((d) => alive && setData(d)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [p.start, p.end, p.between, p.from, p.to, p.pending, timeError]);
  return <CallReportsView company={carrier.name} stage={carrier.stage} p={p} setP={setP} today={today} data={data} error={error} />;
}

export { clockOf };
